import { Fragment } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useEvalConfig, useDisplayConfig } from './hooks';
import {
  newestScoreOfType,
  convertMillipointsToDisplay,
} from '../../utilities/scoring';
import competitorlistscoresheetStyles from './competitorlistscoresheet.module.css';

export function CompetitorListScoresheetRow({ routine }) {
  const evalConfig = useEvalConfig();
  const displayConfig = useDisplayConfig();

  const {
    nDeduct,
    stickBonus,
    dPanel,
    ePanel,
    jPanel,
    svPanel,
    utl,
    utlPanel,
  } = evalConfig;
  const {
    scorePrecision,
    jPrecision,
    ePrecision,
    dPrecision,
    svPrecision,
  } = displayConfig;

  const scores = (routine && routine?.scores?.items) || [];

  const dHeadings = (dPanel && dPanel.map((d) => d.type)) || [];
  const eHeadings = (ePanel && ePanel.map((e) => e.type)) || [];
  const jHeadings = (jPanel && jPanel.map((j) => j.type)) || [];

  const renderHeadings = (headings, precision) => {
    return headings.map((h) => {
      const scoreObj = (scores && newestScoreOfType(scores, h)) || null;
      const scoreVal = h === 'Total' ? routine?.score : scoreObj?.value;

      return (
        <Col key={h} className={competitorlistscoresheetStyles.scoreCol}>
          <span>{h}: </span>
          <div className={competitorlistscoresheetStyles.score}>
            {(scoreVal || scoreVal === 0) &&
              convertMillipointsToDisplay(scoreVal, precision)}
          </div>
        </Col>
      );
    });
  };

  // Render SV+J headings with UTL as separate column (for svPanel configs)
  const renderSvJWithUtl = () => {
    if (!svPanel) return null;
    return svPanel.map((sv, i) => {
      const svScoreObj = newestScoreOfType(scores, sv.type);
      const jType = jHeadings?.[i];
      const jScoreObj = jType ? newestScoreOfType(scores, jType) : null;
      const utlType = utlPanel?.[i]?.type;
      const utlScore = utlType ? newestScoreOfType(scores, utlType) : null;
      const hasUtl = utlScore?.value === 1000;

      return (
        <Fragment key={`svj-utl-${i}`}>
          <Col className={competitorlistscoresheetStyles.scoreCol}>
            <span>{sv.type}: </span>
            <div className={competitorlistscoresheetStyles.score}>
              {(svScoreObj?.value || svScoreObj?.value === 0) &&
                convertMillipointsToDisplay(svScoreObj.value, svPrecision)}
            </div>
          </Col>
          {jType && (
            <Col className={competitorlistscoresheetStyles.scoreCol}>
              <span>{jType}: </span>
              <div className={competitorlistscoresheetStyles.score}>
                {(jScoreObj?.value || jScoreObj?.value === 0) &&
                  convertMillipointsToDisplay(jScoreObj.value, jPrecision)}
              </div>
            </Col>
          )}
          {utl && utlType && (
            <Col
              className={`${competitorlistscoresheetStyles.scoreCol} ${competitorlistscoresheetStyles.utlCol}`}
            >
              <span>
                UTL<sub>{i + 1}</sub>:{' '}
              </span>
              <div className={competitorlistscoresheetStyles.score}>
                <span
                  className={`${competitorlistscoresheetStyles.utlIndicator} ${
                    hasUtl
                      ? competitorlistscoresheetStyles.utlChecked
                      : competitorlistscoresheetStyles.utlUnchecked
                  }`}
                >
                  {hasUtl ? '✓' : ''}
                </span>
              </div>
            </Col>
          )}
        </Fragment>
      );
    });
  };

  // Render J headings with UTL as separate column (for non-svPanel configs)
  const renderJWithUtl = () => {
    if (!jPanel) return null;
    return jPanel.map((j, i) => {
      const scoreObj = newestScoreOfType(scores, j.type);
      const utlType = utlPanel?.[i]?.type;
      const utlScore = utlType ? newestScoreOfType(scores, utlType) : null;
      const hasUtl = utlScore?.value === 1000;

      return (
        <Fragment key={`j-utl-${i}`}>
          <Col className={competitorlistscoresheetStyles.scoreCol}>
            <span>{j.type}: </span>
            <div className={competitorlistscoresheetStyles.score}>
              {(scoreObj?.value || scoreObj?.value === 0) &&
                convertMillipointsToDisplay(scoreObj.value, jPrecision)}
            </div>
          </Col>
          {utl && utlType && (
            <Col
              className={`${competitorlistscoresheetStyles.scoreCol} ${competitorlistscoresheetStyles.utlCol}`}
            >
              <span>
                UTL<sub>{i + 1}</sub>:{' '}
              </span>
              <div className={competitorlistscoresheetStyles.score}>
                <span
                  className={`${competitorlistscoresheetStyles.utlIndicator} ${
                    hasUtl
                      ? competitorlistscoresheetStyles.utlChecked
                      : competitorlistscoresheetStyles.utlUnchecked
                  }`}
                >
                  {hasUtl ? '✓' : ''}
                </span>
              </div>
            </Col>
          )}
        </Fragment>
      );
    });
  };

  return (
    <Row className="scoreSheetSpaces">
      {renderHeadings(dHeadings, svPrecision || dPrecision)}
      {svPanel ? renderSvJWithUtl() : renderJWithUtl()}
      {renderHeadings(eHeadings, ePrecision)}
      {nDeduct ? renderHeadings(['ND'], 1) : null}
      {stickBonus ? renderHeadings(['SB'], 1) : null}
      {renderHeadings(['Total'], scorePrecision)}
    </Row>
  );
}
